<template>
  <article class="post-card">
    <Link :href="route('blog.show', post.slug)" class="post-card-link">
      <figure class="post-card-media">
        <img
          v-if="post.cover_image"
          :src="post.cover_image"
          :alt="post.title"
          loading="lazy"
        />
        <span v-else class="post-card-placeholder" aria-hidden="true">
          <font-awesome-icon :icon="['far', 'file-alt']" />
        </span>

        <time class="post-card-date" :datetime="post.created_at">
          {{ formatDate(post.created_at) }}
        </time>
      </figure>

      <div class="post-card-body">
        <component :is="'h' + level" class="post-card-title">{{ post.title }}</component>
        <p v-if="post.excerpt" class="post-card-excerpt">{{ post.excerpt }}</p>

        <div class="post-card-foot">
          <span class="post-card-author">
            <font-awesome-icon :icon="['far', 'user']" aria-hidden="true" />
            {{ t('By') }} {{ post.author }}
          </span>

          <span class="post-card-action">
            {{ t('Read More') }}
            <font-awesome-icon icon="arrow-right" aria-hidden="true" />
          </span>
        </div>
      </div>
    </Link>
  </article>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* Card on the page ground. The border is 2px rather than 1px: every ground on
   the site is very pale, and a single-pixel hairline all but vanished into
   them. A soft resting shadow lifts the panel off the ground too. Hover and
   keyboard focus deepen the border and the lift. */
.post-card {
  background: $ground;
  border: 2px solid $line-strong;
  border-radius: $r-lg;
  box-shadow: $shadow-card;
  height: 100%;
  overflow: hidden;
  transition: border-color $dur $ease, box-shadow $dur $ease, transform $dur $ease;

  &:hover,
  &:focus-within {
    border-color: $ring-focus;
    box-shadow: $shadow-lift;
    transform: translateY(-4px);
  }

  &:focus-within {
    outline: 3px solid $ring-focus;
    outline-offset: 3px;
  }
}

.post-card-link {
  color: inherit;
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
}

/* Fixed 16:10 media box, so every cover in a row lines up however tall the
   source image is. The blue ground stays visible while a lazy image loads. */
.post-card-media {
  aspect-ratio: 16 / 10;
  background: $ground-accent;
  margin: 0;
  overflow: hidden;
  position: relative;

  img {
    display: block;
    height: 100%;
    object-fit: cover;
    transition: transform $dur-slow $ease;
    width: 100%;
  }
}

.post-card:hover img,
.post-card:focus-within img {
  transform: scale(1.05);
}

.post-card-placeholder {
  align-items: center;
  background: linear-gradient(145deg, $brand-dark 0%, $primary 45%, $brand-bright 100%);
  color: $on-dark-strong;
  display: flex;
  font-size: 2.4rem;
  height: 100%;
  justify-content: center;
  width: 100%;
}

/* Date rides on the cover so the body can open straight on the headline.
   White plaque with a soft 2px edge, echoing the card's own 2px hairline, so
   the badge reads as part of the panel instead of a sticker. Flex-centred, so
   the label sits dead centre in the pill on both axes. */
.post-card-date {
  align-items: center;
  background: $ground;
  border: 2px solid $line-strong;
  border-radius: $r-pill;
  color: $text-heading;
  display: inline-flex;
  font-family: $headings-font-family;
  font-size: 0.68rem;
  font-weight: 600;
  justify-content: center;
  left: 0.9rem;
  letter-spacing: 0.05em;
  padding: 0.4rem 0.75rem;
  position: absolute;
  text-transform: uppercase;
  top: 0.9rem;
}

.post-card-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 1.4rem 1.5rem 1.35rem;
}

.post-card-title {
  color: $text-heading;
  display: -webkit-box;
  font-family: $headings-font-family;
  font-size: 1.2rem;
  line-height: 1.35;
  margin-bottom: 0.6rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.post-card-excerpt {
  color: $text-body;
  display: -webkit-box;
  font-size: 0.92rem;
  line-height: 1.6;
  margin-bottom: 1.25rem;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

/* Pinned to the bottom so the footers line up across a row, whatever the
   headline and excerpt run to. */
.post-card-foot {
  align-items: center;
  border-top: 1px solid $line;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 1rem;
}

.post-card-author {
  align-items: center;
  color: $text-muted;
  display: inline-flex;
  flex: 1 1 auto;
  font-size: 0.8rem;
  gap: 0.4rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  svg {
    flex: 0 0 auto;
  }
}

.post-card-action {
  align-items: center;
  color: $primary;
  display: inline-flex;
  flex: 0 0 auto;
  font-family: $headings-font-family;
  font-size: 0.85rem;
  font-weight: 600;
  gap: 0.4rem;
  white-space: nowrap;

  svg {
    height: 0.8rem;
    transition: transform $dur $ease;
    width: 0.8rem;
  }
}

.post-card:hover .post-card-action svg,
.post-card:focus-within .post-card-action svg {
  transform: translateX(4px);
}
</style>

<script>
import { Link } from '@inertiajs/inertia-vue3';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faFileAlt, faUser } from '@fortawesome/free-regular-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';

FontAwesomeLibrary.add(faArrowRight, faFileAlt, faUser);

export default {
  components: {
    Link
  },

  props: {
    post: {
      type: Object,
      required: true
    },
    /* The card title's heading level, so the card fits where it is used. A card
       in a listing sits one level under the page hero (level 2); the same card
       under a "Related posts" section head is a level 3. Defaults to 3 because
       that is the commoner case, and a hard-coded tag made the listing skip from
       the page h1 straight to an h3. */
    level: {
      type: Number,
      default: 3,
      validator: (value) => Number.isInteger(value) && value >= 1 && value <= 6
    }
  },

  methods: {
    formatDate(isoString) {
      return new Date(isoString).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  },

  setup() {
    const { t } = useI18n({
      messages: {
        'en-MY': {
          'Read More': 'Read More'
        },
        'zh-MY': {
          'Read More': '阅读更多'
        },
        'ms-MY': {
          'Read More': 'Baca Lagi'
        }
      }
    });

    return { t };
  }
};
</script>
